<template>
  <b-container class="trigger-container">
    <b-row>
      <b-col cols="12" class="title">
        <span>First, select what event triggers dynamic scheduling.</span>
        <span class="p-2" id="triggers-info-popover">
          <i class="fa fa-info-circle gray-clr" aria-hidden="true"></i>
        </span>
      </b-col>
      <b-popover target="triggers-info-popover" triggers="hover" placement="right" :boundary="'viewport'">
        <template v-slot:content>
          <div class="popover-font">
            Select the event that will automatically trigger an additional activity for the participant or site. You will be able to configure specific conditions and scheduling in the next steps.
          </div>
        </template>
      </b-popover>
    </b-row>
    <div class="trigger-panel">
      <div v-for="(condition, c_index) in conditions" v-bind:key="'trigger-configurations-' + c_index" class="trigger-select">
        <b-row class="d-flex align-items-center pl-13">
          <b-col cols="3" class="">
            <das-dropdown :options="triggerOptionsFiltered(c_index)" :optionField="'text'" :valueField="'value'" :defaultText="'trigger'" :input="condition.trigger" :id="`trigger-option-dropdown-${c_index}`" @selected-data="setTrigger(condition, c_index, $event)"> </das-dropdown>
          </b-col>

          <!-- Step in activity starts-->
          <b-col cols="4" v-if="condition.trigger === 'StepInActivityCompleted'">
            <dropdown-step-in-activity v-if="taskDetails && taskDetails.questions" :options="getAllowedQuestionsList(c_index)" :optionField="'questionId'" :valueField="'id'" :defaultText="'selectStep'" :input="condition.questionId" :id="`step-in-activity-dropdown-${c_index}`" @selected-data="setStepInActivityStepSelected(condition, c_index, $event)"> </dropdown-step-in-activity>
          </b-col>
          <b-col cols="7" v-if="condition.questionId">
            <span class="step-que" :id="`step-body-${condition.questionId}`">{{ getStepinActivitySelectedStepText(condition.questionId, true) }}</span>

            <b-popover :boundary="'viewport'" :target="`step-body-${condition.questionId}`" triggers="hover" placement="right" v-if="getStepinActivitySelectedStepText(condition.questionId, false).length > 100">
              <template v-slot:content>
                <div class="popover-content">
                  {{ getStepinActivitySelectedStepText(condition.questionId, false) }}
                </div>
              </template>
            </b-popover>
            <div class="mt-6">
              <!-- To select a response for Step in Activity -->
              <das-dropdown :options="getStepinActivityResponseList(condition.questionId)" :optionField="'responseText'" :valueField="'responseText'" :defaultText="'stepinActivityResponse'" :input="changeDropdownValuesBasedOnResponseType(condition)" :id="`step-in-activity-response-dropdown-${c_index}`" @selected-data="setStepInActivityResponseSelected(condition, c_index, $event)">
                >
              </das-dropdown>
            </div>
          </b-col>
          <!-- Step in activity ends-->

          <!-- participant information starts -->

          <b-col cols="3" v-if="condition.trigger === 'Participant'">
            <das-dropdown
              v-if="participantConfigurations && participantConfigurations.registrationConfigWrapper && participantConfigurations.registrationConfigWrapper.participantRegistrationConfigDetails"
              :options="participantConfigurations.registrationConfigWrapper.participantRegistrationConfigDetails"
              :optionField="'field'"
              :valueField="'key'"
              :defaultText="'participantInfo'"
              :input="condition.field"
              :id="`participant-info-option-dropdown-${c_index}`"
              @selected-data="setParicipantField(condition, c_index, $event)"
            >
            </das-dropdown>
          </b-col>

          <!-- Birth Date -->
          <b-col cols="2" v-if="isDateOfBirth(condition.field)">
            <das-dropdown :options="birthDateOperatorList" :optionField="'field'" :valueField="'key'" :defaultText="'birthDate'" :input="condition.operator" :id="`dob-operator-option-dropdown-${c_index}`" @selected-data="setParticipantValue(condition, c_index, $event, 'operatorSelected')"> </das-dropdown>
          </b-col>
          <b-col cols="2" v-show="isDateOfBirth(condition.field)">
            <participant-DOB-section :condition="condition" :index="c_index" @dob-selected="setParticipantValue(condition, c_index, $event, 'dobSelected')" />
          </b-col>

          <!-- Primary Language -->
          <b-col cols="3" v-if="condition.field === 'language'">
            <das-dropdown v-if="participantConfigurations && participantConfigurations.studyLanguage" :options="participantConfigurations.studyLanguage" :optionField="'display_name'" :valueField="'language_id'" :defaultText="'language'" :input="condition.value" :id="`language-option-dropdown-${c_index}`" @selected-data="setParticipantValue(condition, c_index, $event, null)"> </das-dropdown>
          </b-col>

          <!-- Gender -->
          <b-col cols="3" class="d-flex" v-if="condition.field === 'gender'">
            <das-dropdown :options="genderList" :optionField="'values'" :valueField="'identifier'" :defaultText="'gender'" :input="condition.value" :id="`gender-option-dropdown-${c_index}`" @selected-data="setParticipantValue(condition, c_index, $event, null)"> </das-dropdown>

            <i class="fa fa-info-circle gray-clr mt-4" :id="`popover-target-gender-${c_index}`" aria-hidden="true"></i>
            <b-popover :target="`popover-target-gender-${c_index}`" triggers="hover" placement="top">
              <template v-slot:content>
                <div class="popover-font">
                  Select "Other" when the gender is neither male nor female, or was otherwise not identified by the participant.
                </div>
              </template>
            </b-popover>
          </b-col>

          <!-- Country -->
          <b-col cols="3" v-if="condition.field === 'country'">
            <das-dropdown v-if="participantConfigurations && participantConfigurations.countryList" :options="participantConfigurations.countryList" :optionField="'countryName'" :valueField="'countryId'" :defaultText="'country'" :input="condition.value" :id="`country-option-dropdown-${c_index}`" @selected-data="setParticipantValue(condition, c_index, $event, null)"> </das-dropdown>
          </b-col>

          <!-- Age Range -->
          <b-col cols="3" v-if="condition.field === 'ageRange'">
            <das-dropdown :options="ageRange" :optionField="'values'" :valueField="'identifier'" :defaultText="'ageRange'" :input="condition.value" :id="`age-range-option-dropdown-${c_index}`" @selected-data="setParticipantValue(condition, c_index, $event, null)"> </das-dropdown>
          </b-col>
          <!-- participant information ends -->

          <!-- Milestone(s) completed starts -->

          <b-col cols="4" v-if="condition.trigger === 'MilestoneCompleted'">
            <das-dropdown-multi-checkbox
              v-if="participantConfigurations && participantConfigurations.milestonesList"
              :options="participantConfigurations.milestonesList"
              :optionField="'milestoneName'"
              :valueField="'displayName'"
              :defaultText="'milestone'"
              :input="condition.milestones"
              :type="'Milestone'"
              :id="`milestones-option-dropdown-${c_index}`"
              @selected-data="setMileStones(condition, c_index, $event)"
            >
            </das-dropdown-multi-checkbox>
          </b-col>
          <!-- Milestone(s) completed ends -->

          <b-col cols="1" class="ml-auto" v-show="checkBackButtonAndCondition(condition)">
            <button class="btn btn-link color_red mb-11 pr-0" v-on:click="deleteAnotherCondition(c_index)">
              <img height="32" width="32" src="static/img/light-gray-delete.svg" />
            </button>
          </b-col>
        </b-row>

        <b-col cols="12" class="divider-line" v-if="c_index + 1 < conditions.length || (conditions.length < 5 && showAddAnotherConditionButton)"></b-col>
      </div>

      <b-row>
        <b-col v-if="showAddAnotherConditionButton && conditions.length < 5">
          <button class="dynamic-activity-scheduler-btn" @click="addAnotherCondition">
            Add Another Condition
          </button>
        </b-col>
      </b-row>
    </div>
  </b-container>
</template>

<script>
import swal from "sweetalert2";
import dasDropdown from "./dropdown.vue";
import dasDropdownMultiCheckbox from "./dropdown-multi-checkbox.vue";
import dropdownStepInActivity from "./dropdown-step-in-activity";
import ParticipantDOBSection from "./participant-dob-section.vue";
import { TRIGGER_NAME } from "@/constants/dynamicSchedulingConstants";
import { cloneDeep, pick, orderBy } from "lodash";
import momentTimeZone from "moment-timezone";

const TRIGGERS = [
  { text: "Activity is Completed", value: TRIGGER_NAME.ACTIVITY_COMPLETED },
  { text: "Activity was Missed", value: TRIGGER_NAME.ACTIVITY_MISSED },
  { text: "Milestone(s) Completed", value: TRIGGER_NAME.MILESTONE_COMPLETED },
  { text: "Participant Information", value: TRIGGER_NAME.PARTICIPANT },
  {
    text: "Step in Activity is Completed",
    value: TRIGGER_NAME.STEP_IN_ACTIVITY_COMPLETED
  }
];

const DEFAULT_CONDITION = {
  trigger: "",
  isPrimaryCondition: false,
  field: "",
  value: "",
  operator: "",
  milestones: ""
};

export default {
  name: "trigger-configuration-step",
  components: {
    dasDropdown,
    dasDropdownMultiCheckbox,
    dropdownStepInActivity,
    ParticipantDOBSection
  },
  data() {
    return {
      triggerOptions: [...TRIGGERS],
      genderList: [],
      birthDateOperatorList: [
        { field: "Does Not Equal", key: "!=" },
        { field: "Is Equal To", key: "=" },
        { field: "Is Greater Than", key: ">" },
        { field: "Is Less Than", key: "<" }
      ],
      ageRange: [],
      selectedShowOption: null,
      conditions: [],
      showAddAnotherConditionButton: false,
      dynamicStepInActivityResponseInput: null,
      participantConfigurations: null,
      currentParentCondition: null,
      isBackButtonClicked: false
    };
  },
  props: {
    triggersRelatedDetails: {
      type: Object,
      required: false,
      default: null
    },
    editConditionsData: {
      type: Array,
      required: false
    },
    taskDetails: {
      type: [Object, String],
      default: null
    }
  },
  watch: {
    editConditionsData: {
      deep: true,
      immediate: true,
      handler: function(newData, oldData) {
        this.conditions = [];
        if (newData && newData.length) {
          /* when Edit old dynamic schedule */
          this.conditions = cloneDeep(newData);
          for (const condt of this.conditions) {
            /* We had to swap 'month' with 'date' here as vue-datepicker library is not accepting date in DD/MM/YYYY format & changes it to MM/DD/YYYY format automatically */
            if (condt.trigger === TRIGGER_NAME.PARTICIPANT && this.isDateOfBirth(condt.field)) {
              let date = null;
              const validDate = condt["value"].match(/^((0[1-9]|1[0-9]|2[0-9]|3[0-1])[\s](JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)[\s](\d{4}))$/);
              if (validDate) {
                date = condt["value"];
              } else {
                const digits = condt["value"].split("/");
                date = digits[1] + "/" + digits[0] + "/" + digits[2];
              }
              condt["value"] = momentTimeZone(date)
                .format("DD MMM YYYY")
                .toUpperCase();
            }
          }
          if (this.checkMilestoneAndPartRegFielEmpty() === true) {
            this.showAddAnotherConditionButton = false;
          } else {
            this.showAddAnotherConditionButton = true; /* to show 'Add Another Condition' button */
          }
          this.currentParentCondition = this.conditions[0].trigger;
        } else {
          /* when Add new dynamic schedule */
          this.conditions && this.conditions.push(cloneDeep(DEFAULT_CONDITION));
          this.conditions && this.conditions.length && (this.conditions[0]["isPrimaryCondition"] = true);
          this.showAddAnotherConditionButton = false; /* to hide 'Add Another Condition' button */
          this.currentParentCondition = null;
          if (oldData) {
            this.conditions[0].trigger = oldData[0].trigger ? oldData[0].trigger : "";
          } else {
            this.conditions[0].trigger = "";
          }

          if (this.checkMilestoneAndPartRegFielEmpty() === true) {
            this.showAddAnotherConditionButton = false;
          }
        }
        this.$forceUpdate();
      }
    },
    triggersRelatedDetails: {
      deep: true,
      immediate: true,
      handler: function(newData) {
        if (newData) {
          if (newData.registrationConfigWrapper && newData.registrationConfigWrapper.participantRegistrationConfigDetails) {
            if (newData.registrationConfigWrapper.participantRegistrationConfigDetails.length) {
              const regs = newData.registrationConfigWrapper.participantRegistrationConfigDetails;
              for (let i = 0; i < regs.length; i++) {
                if (regs[i] && regs[i].key && regs[i].key === "birthDate") {
                  const reg = cloneDeep(regs[i]);
                  reg["key"] = "dob";
                  regs.splice(i, 1, reg);
                }

                if (regs[i] && regs[i].key && regs[i].key === "gender") {
                  this.genderList = regs[i].lookupExpectedValue;
                }

                if (regs[i] && regs[i].key && regs[i].key === "ageRange") {
                  this.ageRange = regs[i].lookupExpectedValue;
                }

                if (regs[i] && regs[i].key && regs[i].key === "language") {
                  regs[i]["field"] = "Primary Language";
                }
              }
            } else {
              /* if ageRange,country,dob,langs,gender are not configured then, Participant Info doesn't show */
              this.triggerOptions = this.triggerOptions.filter(trigger => {
                return trigger.value !== TRIGGER_NAME.PARTICIPANT;
              });
            }
          }

          if (!newData.milestonesEnabled) {
            /* if no milestones are configured then, Milestones Completed doesn't show */
            this.triggerOptions = this.triggerOptions.filter(trigger => {
              return trigger.value !== TRIGGER_NAME.MILESTONE_COMPLETED;
            });
          }

          /* Sorting different 'Participant Information' dropdown values - starts */

          this.participantConfigurations = cloneDeep(newData);

          this.participantConfigurations["countryList"] = orderBy(this.participantConfigurations["countryList"], [country => country["countryName"].toLowerCase()], ["asc"]);

          this.participantConfigurations["studyLanguage"] = orderBy(this.participantConfigurations["studyLanguage"], [language => language["display_name"].toLowerCase()], ["asc"]);

          this.participantConfigurations["registrationConfigWrapper"]["participantRegistrationConfigDetails"] = orderBy(this.participantConfigurations["registrationConfigWrapper"]["participantRegistrationConfigDetails"], [participantInfo => participantInfo["field"].toLowerCase()], ["asc"]);

          /* Sorting different 'Participant Information' dropdown values - ends */
        }
      }
    },
    taskDetails: {
      deep: true,
      immediate: true,
      handler: function() {}
    }
  },
  mounted() {
    this.$root.$on("ds-back-button-clicked", data => {
      if (data == true) {
        this.emitEventToDisableNextButton(false);
        this.isBackButtonClicked = data;
      }
    });
  },
  computed: {},
  methods: {
    addAnotherCondition() {
      this.conditions && this.conditions.push(cloneDeep(DEFAULT_CONDITION));
      this.emitEventToDisableNextButton(true);
    },
    deleteAnotherCondition(index) {
      swal({
        title: "Are you sure you want to delete this condition?",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#b8c7ce",
        cancelButtonColor: "#f16559",
        confirmButtonText: "Cancel",
        cancelButtonText: "Delete"
      }).then(({ dismiss }) => {
        if (dismiss === "cancel") {
          this.conditions && this.conditions.splice(index, 1);
          this.validateTrigger();
        }
      });
    },
    triggerOptionsFiltered(idx) {
      let options = [];
      if (idx === 0) {
        options = this.triggerOptions.filter(opt => {
          return opt.value === TRIGGER_NAME.ACTIVITY_COMPLETED || opt.value === TRIGGER_NAME.ACTIVITY_MISSED || opt.value === TRIGGER_NAME.STEP_IN_ACTIVITY_COMPLETED;
        });
      } else {
        const activityCompleted = this.conditions && this.conditions.some(ele => ele.trigger === TRIGGER_NAME.ACTIVITY_COMPLETED);
        const activityMissed = this.conditions && this.conditions.some(ele => ele.trigger === TRIGGER_NAME.ACTIVITY_MISSED);
        const stepInActivityCompleted = this.conditions && this.conditions.some(ele => ele.trigger === TRIGGER_NAME.STEP_IN_ACTIVITY_COMPLETED);

        if (activityCompleted || stepInActivityCompleted) {
          options = this.triggerOptions.filter(opt => opt.value !== TRIGGER_NAME.ACTIVITY_MISSED);
        } else if (activityMissed) {
          options = this.triggerOptions.filter(opt => {
            return opt.value !== TRIGGER_NAME.ACTIVITY_COMPLETED && opt.value !== TRIGGER_NAME.STEP_IN_ACTIVITY_COMPLETED;
          });
        }
      }
      options = this.getAllowedTriggerList(options, idx);

      return options;
    },
    setTrigger(condition, i, event) {
      if (i === 0 && !!this.conditions && this.conditions.length > 1 && !!this.conditions[i].trigger && (this.conditions[i + 1].trigger === undefined || this.conditions[i + 1].trigger === "") && this.conditions[0].trigger !== event.value) {
        this.conditions = [];
        this.conditions && this.conditions.push(cloneDeep(DEFAULT_CONDITION));
      }
      if (this.conditions[i].trigger && this.conditions[i].trigger === event.value) {
        return null;
      }
      let condt = null;

      if (i === 0 && this.conditions.length && this.conditions[0].trigger === TRIGGER_NAME.STEP_IN_ACTIVITY_COMPLETED) {
        condt = cloneDeep(condition);
      } else {
        condt = pick(condition, ["trigger", "isPrimaryCondition"]);
      }

      if (i === 0 && (event.value === TRIGGER_NAME.ACTIVITY_COMPLETED || event.value === TRIGGER_NAME.ACTIVITY_MISSED || event.value === TRIGGER_NAME.STEP_IN_ACTIVITY_COMPLETED)) {
        this.currentParentCondition = event.value;
      }

      if (i === 0 && this.conditions && this.conditions.length > 1 && this.conditions[0].trigger && this.conditions[1].trigger && this.conditions[0].trigger !== this.currentParentCondition) {
        swal({
          title: "Are you sure you want to change the event trigger?<br>Any change will remove all conditions.",
          type: "warning",
          showCancelButton: true,
          confirmButtonColor: "#b8c7ce",
          cancelButtonColor: "#4c8ce4",
          confirmButtonText: "Cancel",
          cancelButtonText: "Yes"
        }).then(({ dismiss }) => {
          if (dismiss === "cancel") {
            this.conditions.splice(1, this.conditions.length);
            condt = pick(condition, ["trigger", "isPrimaryCondition"]);
            condt["trigger"] = event.value;
            this.currentParentCondition = event.value;
            this.conditions[i] = condt;
            if (this.checkMilestoneAndPartRegFielEmpty()) {
              this.emitEventToDisableNextButton(false);
            }
            this.$forceUpdate();
          }
        });
      } else {
        condt = pick(condition, ["trigger", "isPrimaryCondition"]);
        condt["trigger"] = event.value;
      }
      if (i === 0) {
        condt["isPrimaryCondition"] = true;
      } else {
        condt["isPrimaryCondition"] = false;
      }
      this.conditions[i] = condt;
      this.$forceUpdate();
      this.validateTrigger();
    },
    setParicipantField(condition, i, event) {
      if (this.conditions[i].field && this.conditions[i].field === event.key) {
        return null;
      }

      const condt = pick(condition, ["trigger", "isPrimaryCondition", "field"]);
      condt["field"] = event.key;
      this.conditions[i] = condt;
      this.$forceUpdate();
      this.validateTrigger();
    },
    setParticipantValue(condition, i, event, whatToDo) {
      let condt = null;
      if (condition.field && condition.field === "gender") {
        condt = pick(condition, ["trigger", "isPrimaryCondition", "field", "value"]);
        condt["value"] = event.identifier;
      } else if (condition.field && condition.field === "country") {
        condt = pick(condition, ["trigger", "isPrimaryCondition", "field", "value"]);
        condt["value"] = event.countryId;
      } else if (condition.field && condition.field === "language") {
        condt = pick(condition, ["trigger", "isPrimaryCondition", "field", "value"]);
        condt["value"] = event.language_id;
      } else if (condition.field && condition.field === "ageRange") {
        condt = pick(condition, ["trigger", "isPrimaryCondition", "field", "value"]);
        condt["value"] = event.identifier;
      } else if (condition.field && this.isDateOfBirth(condition.field)) {
        condt = pick(condition, ["trigger", "isPrimaryCondition", "field", "operator", "value"]);
        condt["value"] = condt["value"] ? condt["value"] : null;
        if (whatToDo === "operatorSelected") {
          /* When operator is selected */
          condt["operator"] = event.key;
        } else if (whatToDo === "dobSelected") {
          /* When date of birth is selected */
          if (event !== condt["value"] && event != null && event !== "INVALID DATE") {
            condt["value"] = event;
          }
        }
      }
      this.conditions[i] = condt;
      this.$forceUpdate();
      this.validateTrigger();
    },
    setMileStones(condition, i, event) {
      const condt = pick(condition, ["trigger", "isPrimaryCondition", "milestones"]);
      condt["milestones"] = event;
      this.conditions[i] = condt;
      this.$forceUpdate();
      this.validateTrigger();
    },
    getStepinActivityResponseList(questionId) {
      const responses = [
        { responseText: "Any Response", value: "anyResponse" },
        { responseText: "No Response", value: "noResponse" }
      ];
      const selectedQuestion = this.taskDetails.questions.filter(que => {
        return que.id === questionId;
      });
      if (selectedQuestion && selectedQuestion[0]) {
        const que = selectedQuestion[0];
        switch (que.type) {
          case "DropDown":
            que.options.forEach(option => {
              responses.push({ responseText: option.body, value: option });
            });
            break;
          case "MultiChoice":
            que.options.forEach(option => {
              responses.push({ responseText: option.body, value: option });
            });
            break;
          case "Boolean":
            que.options.forEach(option => {
              responses.push({ responseText: option.body, value: option });
            });
            break;
          default:
            break;
        }
      }
      return responses;
    },
    getStepinActivitySelectedStepText(questionId, showTruncatedText) {
      const selectedQuestion = this.taskDetails.questions.filter(que => {
        return que.id === questionId;
      });
      const queText = selectedQuestion && selectedQuestion[0] && selectedQuestion[0].body;
      return showTruncatedText ? (queText.length > 100 ? queText.substring(0, 101) + "..." : queText) : queText;
    },
    getAllowedQuestionsList(index) {
      /* This is for not showing questions that are already once selected. */

      let questionsList = cloneDeep(this.taskDetails.questions);
      const conditions = this.conditions;
      for (let i = 0; i < conditions.length; i++) {
        if (conditions[i].trigger === TRIGGER_NAME.STEP_IN_ACTIVITY_COMPLETED) {
          for (let q = 0; q < this.taskDetails.questions.length; q++) {
            if (!conditions[i].questionId) {
              break;
            }
            if (index === i) {
              break;
            }
            if (conditions[i].questionId === this.taskDetails.questions[q].id) {
              if (this.taskDetails.questions[q].type !== "MultiChoice" || (this.taskDetails.questions[q].type === "MultiChoice" && !this.taskDetails.questions[q].multipleSelection)) {
                questionsList = questionsList.filter(q => q.id !== conditions[i].questionId);
                break;
              }
            }
          }
        }
      }
      return questionsList;
    },
    setStepInActivityStepSelected(condition, i, event) {
      if (this.conditions[i].questionId && this.conditions[i].questionId === event.id) {
        return null;
      }

      const condt = pick(condition, ["trigger", "isPrimaryCondition", "questionId"]);
      const stepNumber = event.stepSequence ? event.stepSequence : event.sequence;
      condt["questionId"] = event.id;
      condt["step"] = event.body;
      condt["stepNumber"] = stepNumber;
      this.conditions[i] = condt;
      this.$forceUpdate();
      this.validateTrigger();
    },
    setStepInActivityResponseSelected(condition, i, event) {
      const condt = pick(condition, ["trigger", "isPrimaryCondition", "questionId", "step", "stepNumber"]);
      condt["responseOptionId"] = event.value.id ? event.value.id : null;
      condt["responseText"] = event.value.body ? event.value.body : null;
      condt["noResponse"] = this.isNoResponse(event);
      condt["anyResponse"] = this.isAnyResponse(event);
      this.conditions[i] = condt;
      this.$forceUpdate();
      this.validateTrigger();
    },
    changeDropdownValuesBasedOnResponseType(condition) {
      if (condition && condition.trigger === TRIGGER_NAME.STEP_IN_ACTIVITY_COMPLETED) {
        if (condition.responseText) {
          /* When response is taken from Step options */
          return condition.responseText;
        } else if (condition.anyResponse) {
          /* When response is 'Any Response' */
          return "Any Response";
        } else if (condition.noResponse) {
          /* When response is 'No Response' */
          return "No Response";
        } else {
          /* When there is a change in Step */
          return null;
        }
      }
    },
    isNoResponse(event) {
      return !!(event.value && event.value === "noResponse");
    },
    isAnyResponse(event) {
      return !!(event.value && event.value === "anyResponse");
    },
    isDateOfBirth(key) {
      return key === "dob" || key === "birthDate" || key === "dobWithMonthYear" || key === "dobWithYear";
    },
    emitEventToDisableNextButton(value) {
      if (this.checkMilestoneAndPartRegFielEmpty()) {
        this.showAddAnotherConditionButton = value;
      } else {
        this.showAddAnotherConditionButton = !value;
      }

      this.$emit("disableNextButton", value);
    },
    validateTrigger() {
      let disableNext = true;

      let condition = null;

      for (let i = 0; i < this.conditions.length; i++) {
        disableNext = true;
        condition = this.conditions[i];
        switch (condition.trigger) {
          case TRIGGER_NAME.ACTIVITY_COMPLETED:
            disableNext = false;
            break;
          case TRIGGER_NAME.ACTIVITY_MISSED:
            disableNext = false;
            break;
          case TRIGGER_NAME.MILESTONE_COMPLETED:
            if (condition.milestones && condition.milestones.length) {
              disableNext = false;
            }
            break;
          case TRIGGER_NAME.PARTICIPANT:
            switch (condition.field) {
              case "language":
                if (condition.value && typeof condition.value === "string") {
                  disableNext = false;
                }
                break;
              case "gender":
                if (condition.value && typeof condition.value === "string") {
                  disableNext = false;
                }
                break;
              case "country":
                if (condition.value && typeof condition.value === "string") {
                  disableNext = false;
                }
                break;
              case "ageRange":
                if (condition.value && typeof condition.value === "string") {
                  disableNext = false;
                }
                break;
              case "dob":
              case "dobWithMonthYear":
              case "dobWithYear":
                if (condition.operator && condition.value && (typeof condition.value === "string" || typeof condition.value === "object") && this.validateDOBFormat(condition.value)) {
                  disableNext = false;
                }
                break;
            }
            break;
          case TRIGGER_NAME.STEP_IN_ACTIVITY_COMPLETED:
            if ((condition.responseOptionId && typeof condition.responseOptionId === "string") || condition.anyResponse || condition.noResponse) {
              disableNext = false;
            }
            break;
        }

        if (disableNext) {
          break;
        }
      }
      this.emitEventToDisableNextButton(disableNext);
    },
    validateDOBFormat(dateString) {
      if (!dateString) {
        return false;
      }

      if (this.typeOf(dateString) === "String") {
        const validDate = dateString.match(/^((0[1-9]|1[0-9]|2[0-9]|3[0-1])[\s](JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)[\s](\d{4}))$/);
        return validDate;
      } else {
        return false;
      }
    },
    typeOf(value) {
      if (Object.prototype.toString.call(value) === "[object Date]") {
        return "Date";
      } else if (Object.prototype.toString.call(value) === "[object Object]") {
        return "Object";
      } else if (Object.prototype.toString.call(value) === "[object String]") {
        return "String";
      } else {
        return value;
      }
    },
    getAllowedTriggerList(triggerOptions, index) {
      /* This is for not showing triggers that are already once selected. */

      let triggerList = cloneDeep(triggerOptions);
      for (let i = 0; i < this.conditions.length; i++) {
        if (!this.conditions[i].trigger) {
          continue;
        }
        if (index === i) {
          continue;
        }
        if (this.conditions[i].trigger === TRIGGER_NAME.ACTIVITY_COMPLETED) {
          triggerList = triggerList.filter(t => t.value !== TRIGGER_NAME.ACTIVITY_COMPLETED);
        }
        if (this.conditions[i].trigger === TRIGGER_NAME.ACTIVITY_MISSED) {
          triggerList = triggerList.filter(t => t.value !== TRIGGER_NAME.ACTIVITY_MISSED);
        }
        if (this.conditions[i].trigger === TRIGGER_NAME.MILESTONE_COMPLETED) {
          triggerList = triggerList.filter(t => t.value !== TRIGGER_NAME.MILESTONE_COMPLETED);
        }
      }
      return triggerList;
    },
    checkBackButtonAndCondition(condition) {
      let flag = false;
      if ((this.isBackButtonClicked && !!condition && !!condition.trigger && condition.isPrimaryCondition == false) || !condition.isPrimaryCondition) {
        flag = true;
      }
      return flag;
    },
    checkMilestoneAndPartRegFielEmpty() {
      let flag = false;
      if (!!this.conditions && this.conditions[0].trigger == "ActivityMissed" && Object.keys(this.participantConfigurations).length != 0 && !this.participantConfigurations.milestonesEnabled && this.participantConfigurations.registrationConfigWrapper.participantRegistrationConfigDetails.length == 0) {
        flag = true;
      }
      return flag;
    }
  }
};
</script>
<style lang="scss" scoped>
.trigger-container {
  .display-block {
    display: block;
  }

  .display-none {
    display: none;
  }
  .trigger-dropdown {
    border: solid 1px #d3dfe4;
    padding: 6px 17px;
    border-radius: 3px;
    position: relative;

    .caret {
      width: 0;
      height: 0;
      border-left: 7px solid transparent;
      border-right: 7px solid transparent;
      border-top: 6px solid #b0bac9;
      top: 14px;
      right: 7px;
      position: absolute;
    }

    ul {
      font-family: Arial, Verdana;
      font-size: 14px;
      margin: 0;
      padding: 0;
      list-style: none;
      position: absolute;
      z-index: 9;
      box-shadow: 0 1px 0px 0 rgba(230, 230, 230, 2%), 0 1px 9px 0 rgba(230, 230, 230, 100%);
      li {
        display: block;
        text-decoration: none;
        color: #000;
        /* border-top: 1px solid #ffffff; */
        padding: 5px 15px 5px 15px;
        background: #ffffff;
        margin-left: 1px;
        white-space: nowrap;
      }
    }
  }

  select {
    background-position: right 50% !important;
    background-repeat: no-repeat;
    background-image: url(/static/img/arrow-down.svg);
    position: relative !important;
  }
  & .options-cls {
    position: absolute !important;
    top: 0 !important;
  }

  .trigger-panel {
    padding: 0 0 160px;
    max-height: 450px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  padding: 21px 0;
  max-height: 507px;
  & .title {
    margin-bottom: 20px;
    padding-left: 28px;
    span {
      font-family: RobotoRegular;
      font-size: 16px;
      color: #000;
    }
  }
  & .gray-clr {
    color: #bac1c4;
    margin-left: 4px;
    font-size: 16px;
  }
  & .trigger-select {
    .step-que {
      color: #959fa4;
      font-size: 12px;
      font-weight: 500;
      margin: 0 10px 7px;
    }
    .datepicker-class {
      background-color: #fff !important;
    }
  }

  & .color_red {
    padding-top: 0px;

    img {
      vertical-align: middle;
      padding: 2px;
      border: 1px solid #d3dfe4;
      background-color: rgba(211, 223, 228, 0.8);
      opacity: 0.8;
      height: 100%;
      border-radius: 3px;
      &:hover {
        cursor: pointer;
        box-shadow: 0 1px 0px 0 rgba(230, 230, 230, 2%), 0 1px 9px 0 rgba(230, 230, 230, 19%);
      }
      &:active {
        cursor: pointer;
        box-shadow: none;
      }
    }
  }
  & .divider-line {
    margin-top: 17px;
    margin-bottom: 32px;
    background-color: #c5d1d6;
    height: 1px;
    width: 100%;
  }
  & .dynamic-activity-scheduler-btn {
    border-radius: 4px;
    border: solid 1px #4c8ce4;
    background: #fff;
    font-family: "RobotoRegular";
    font-size: 15px;
    color: #4c8ce4;
    padding: 3px 12px 4px 12px;
    position: relative;
    margin-left: 15px;
    &:hover {
      cursor: pointer;
      color: #649be8;
      border: solid 1px #649be8;
      box-shadow: 0 1px 0px 0 rgba(230, 230, 230, 2%), 0 1px 9px 0 rgba(230, 230, 230, 19%);
    }
    &:active {
      cursor: pointer;
      color: #4c8ce4;
      border: solid 1px #4c8ce4;
      box-shadow: none;
    }
  }
}

.popover-content {
  font-size: 13px;
  font-family: "RobotoRegular";
  color: #000;
  margin: 6px 7px;
}

.trigger-panel::-webkit-scrollbar-thumb {
  background-color: #c4c4c4;
  border-radius: 4px;
  height: 50px;
  border: 3px solid transparent;
}

.trigger-panel::-webkit-scrollbar-track {
  background: transparent;
}

.trigger-panel::-webkit-scrollbar {
  width: 6px;
}

.popover-font {
  font-size: 13px;
  font-family: "RobotoRegular";
  color: #000;
}
</style>
